import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import ProtectedRoute from './routes/ProtectedRoute';
import useVisitorCounter from './hooks/useVisitorCounter';

// Public Pages
import Home from './pages/public/Home/Home';
import ProductsPage from './pages/public/Products/ProductsPage';
import ProductDetailsPage from './pages/public/ProductDetail/ProductDetail';
import Contact from './pages/public/Contact/Contact';
import CategoriesPage from './pages/public/Categories/CategoriesPage';
import PrivacyPolicy from './pages/public/PrivacyPolicy/PrivacyPolicy';
import TermsConditions from './pages/public/TermsConditions/TermsConditions';
import CookiesPolicy from './pages/public/CookiesPolicy/CookiesPolicy';
import FAQ from './pages/public/FAQ/FAQ';
import About from './pages/public/About/About';
import NotFound from './pages/public/NotFound/NotFound';

// Auth Pages
import Login from './pages/auth/Login/Login';
import ForgotPassword from './pages/auth/ForgotPassword/ForgotPassword';

// Admin Pages
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard/AdminDashboard';
import AdminProducts from './pages/admin/AdminProducts/AdminProducts';
import AdminSeedPage from './pages/admin/AdminSeedPage/AdminSeedPage';
import AdminOrders from './pages/admin/AdminOrders/AdminOrders';
import AdminCategories from './pages/admin/AdminCategories/AdminCategories';
import AdminHomeProducts from './pages/admin/adminHomeProducts/adminHomeProducts';
import AdminComments from './pages/admin/AdminComments/AdminComments';
import AdminMessages from './pages/admin/AdminMessages/AdminMessages';
import AdminInvoices from './pages/admin/AdminInvoices/AdminInvoices';
import AdminSettings from './pages/admin/AdminSettings/AdminSettings';

import './styles/variables.css';
import './styles/themes/light.css';
import './styles/themes/dark.css';
import './App.css';

// Visitor Tracking Component
const VisitorTracker = () => {
  const { isNewVisitor, visitorStats } = useVisitorCounter();
  
  // Optional: You can display a welcome message for new visitors
  // This component doesn't render anything, it just tracks
  return null;
};

function App() {
  return (
    <ToastProvider>
      <ThemeProvider>
        <AuthProvider>
          <Router>
            <VisitorTracker />
            <div className="app">
              <main className="main-content">
                <Routes>
                  {/* Public Routes */}
                  <Route path="/" element={<Home />} />
                  <Route path="/products" element={<ProductsPage />} />
                  <Route path="/product/:id" element={<ProductDetailsPage />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/categories/:categoryName" element={<CategoriesPage />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/terms" element={<TermsConditions />} />
                  <Route path="/cookies" element={<CookiesPolicy />} />
                  <Route path="/faq" element={<FAQ />} />

                  {/* Auth Routes */}
                  <Route path="/login" element={<Login />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />

                  {/* Protected Admin Routes */}
                  <Route
                    path="/admin/*"
                    element={
                      <ProtectedRoute requireAdmin={true}>
                        <AdminLayout />
                      </ProtectedRoute>
                    }
                  >
                    <Route path="" element={<AdminDashboard />} />
                    <Route path="products" element={<AdminProducts />} />
                    <Route path="seed" element={<AdminSeedPage />} />
                    <Route path="commands" element={<AdminOrders />} />
                    <Route path="categories" element={<AdminCategories />} />
                    <Route path="home-products" element={<AdminHomeProducts />} />
                    <Route path="comments" element={<AdminComments />} />
                    <Route path="messages" element={<AdminMessages />} />
                    <Route path="invoices" element={<AdminInvoices />} />
                    <Route path="settings" element={<AdminSettings />} />
                  </Route>

                  {/* 404 Route */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </main>
            </div>
          </Router>
        </AuthProvider>
      </ThemeProvider>
    </ToastProvider>
  );
}

export default App;